import {observer} from "mobx-react";
import {useCallback, useContext, useMemo} from "react";
import {TextField, Typography, Button, IconButton, CircularProgress} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import SaveIcon from "@mui/icons-material/Save";
import ImageIcon from "@mui/icons-material/Image";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {useDropzone} from "react-dropzone";
import {useNavigate} from "react-router";
import {Frame} from "../../common/Frame";
import {LessonEditorStoreContext} from "../../../stores/LessonEditorStore";
import {AppContext} from "../../../stores/AppStore";
import {UploadStore} from "../../../stores/UploadStore";
import {AppRoutes} from "../../../AppRoutes";
import {D} from "../../../Constants";

const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp']);

export const LessonEditorHeader = observer(() => {
  const store = useContext(LessonEditorStoreContext);
  const app = useContext(AppContext)!;
  const theme = useTheme();
  const navigate = useNavigate();

  const uploadStore = useMemo(() => new UploadStore(app, IMAGE_EXTENSIONS), [app]);

  const handleDeleteLesson = () => {
    app.confirmationDialogText = `Delete "${store?.lesson?.title}"?`;
    app.confirmationDialogAction = async () => {
      const success = await store?.deleteLesson();
      if (success) {
        navigate(AppRoutes.lessons, {replace: true});
      }
    };
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    uploadStore.uploadFile(acceptedFiles).then(() => {
      if (uploadStore.uploadedFile && store?.lesson) {
        store.lesson.thumbnailUrl = uploadStore.uploadedFile.preview ?? undefined;
        store.thumbnailFileId = uploadStore.uploadedFile.id ?? undefined;
      }
    });
  }, [uploadStore, store]);

  const {getRootProps, getInputProps, isDragActive} = useDropzone({
    onDrop,
    accept: {'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp']},
    maxFiles: 1,
    disabled: uploadStore.started && !uploadStore.done,
  });

  const handleRemoveThumbnail = () => {
    if (store?.lesson) {
      store.lesson.thumbnailUrl = "";
      store.thumbnailFileId = undefined;
    }
    uploadStore.cancel();
  };

  if (!store || !store.lesson) return null;

  const hasThumbnail = !!store.lesson.thumbnailUrl;

  return <>
    <Frame
      direction="column"
      gap={2}
      padding={3}
      appearance="card"
    >
      <Frame row gap={3}>
        {/* Thumbnail Upload */}
        {hasThumbnail ? (
          <Frame
            style={{
              width: 250,
              height: 250,
              flexShrink: 0,
              position: 'relative',
            }}
          >
            <img
              src={store.lesson.thumbnailUrl!}
              alt="Thumbnail"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
                borderRadius: D.radius(1),
              }}
            />
            <IconButton
              onClick={handleRemoveThumbnail}
              size="small"
              style={{
                position: 'absolute',
                bottom: 4,
                right: 4,
                backgroundColor: 'rgba(255,255,255,0.9)',
              }}
            >
              <DeleteIcon fontSize="small" color="error"/>
            </IconButton>
          </Frame>
        ) : (
          <Frame
            {...getRootProps()}
            clickable={!uploadStore.started || uploadStore.done}
            radius={1}
            center
            style={{
              border: `2px dashed ${isDragActive ? theme.palette.primary.main : uploadStore.started && !uploadStore.done ? theme.palette.primary.main : theme.palette.divider}`,
              backgroundColor: isDragActive ? theme.palette.action.hover : 'transparent',
              transition: 'all 0.2s',
              width: 250,
              height: 250,
              flexShrink: 0,
            }}
          >
            <input {...getInputProps()} />
            {uploadStore.started && !uploadStore.done ? (
              <Frame direction="column" center gap={1}>
                <Frame style={{position: 'relative', display: 'inline-flex'}}>
                  <CircularProgress
                    variant="determinate"
                    value={(uploadStore.progress ?? 0) * 100}
                    size={48}
                  />
                  <Frame
                    center
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                    }}
                  >
                    <Typography variant="caption" color="primary" fontWeight={600}>
                      {Math.round((uploadStore.progress ?? 0) * 100)}%
                    </Typography>
                  </Frame>
                </Frame>
                <Typography variant="caption" color="text.secondary">
                  Uploading...
                </Typography>
              </Frame>
            ) : (
              <Frame direction="column" center gap={0.5}>
                {isDragActive ? (
                  <CloudUploadIcon style={{fontSize: 32, color: theme.palette.primary.main}}/>
                ) : (
                  <ImageIcon style={{fontSize: 32, color: theme.palette.text.secondary}}/>
                )}
                <Typography variant="caption" color="text.secondary" style={{textAlign: 'center'}}>
                  {isDragActive ? 'Drop image' : 'Upload\nthumbnail'}
                </Typography>
              </Frame>
            )}
          </Frame>
        )}

        {/* Title & Description */}
        <Frame direction="column" gap={2} grow>
          <TextField
            fullWidth
            label="Title"
            value={store.lesson.title ?? ""}
            onChange={(e) => {
              if (store.lesson) {
                store.lesson.title = e.target.value;
              }
            }}
            size="small"
          />

          <TextField
            fullWidth
            label="Description"
            value={store.lesson.description ?? ""}
            onChange={(e) => {
              if (store.lesson) {
                store.lesson.description = e.target.value;
              }
            }}
            size="small"
            multiline
            minRows={3}
            maxRows={6}
          />
        </Frame>
      </Frame>

      <Frame row justifyContent="space-between" alignItems="center">
        {store.statistics && (
          <Frame row gap={3}>
            <Typography variant="body2" color="textSecondary">
              {store.statistics.chapters} chapters
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {store.statistics.videoExamples} video examples
            </Typography>
            {store.statistics.durationMinutes > 0 && (
              <Typography variant="body2" color="textSecondary">
                ~{store.statistics.durationMinutes} min
              </Typography>
            )}
          </Frame>
        )}
        <Frame row gap={1} alignItems="center">
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon/>}
            onClick={handleDeleteLesson}
          >
            Delete Lesson
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon/>}
            onClick={() => store.updateLesson()}
            disabled={store.saving}
          >
            {store.saving ? 'Save' : 'Save'}
          </Button>
        </Frame>
      </Frame>
    </Frame>
  </>;
});
